import type { ItemDto } from "@home/shared";
import { useState } from "react";
import { api, fileUrl } from "../api.ts";
import { t, UNIT_CODES, unitLabel } from "../i18n.ts";
import { compressImage } from "../photo.ts";
import { engine, useSync } from "../store.ts";
import { Sheet } from "./Sheet.tsx";

export function ItemEditor({ item, token, onClose }: { item: ItemDto; token: string; onClose: () => void }) {
  const { status } = useSync();
  const [name, setName] = useState(item.name);
  const [qty, setQty] = useState(item.qty?.toString() ?? "");
  const [unit, setUnit] = useState(item.unit ?? "");
  const [comment, setComment] = useState(item.comment ?? "");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const online = status === "online" || status === "syncing";

  const save = () => {
    const qtyNum = qty.trim() ? Number(qty.replace(",", ".")) : null;
    const patch = {
      name: name.trim() !== item.name && name.trim() ? name.trim() : undefined,
      qty: qtyNum !== item.qty && (qtyNum === null || qtyNum > 0) ? qtyNum : undefined,
      unit: (unit || null) !== item.unit ? unit || null : undefined,
      comment: (comment.trim() || null) !== item.comment ? comment.trim() || null : undefined,
    };
    if (Object.values(patch).some((v) => v !== undefined)) void engine.mutate({ type: "item.update", id: item.id, ...patch });
    onClose();
  };

  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const { id } = await api.uploadPhoto(await compressImage(file));
      await engine.mutate({ type: "item.update", id: item.id, photoFileId: id });
    } catch {
      setError(t("photoOffline"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Sheet title={item.name} onClose={save}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <label className="field">
          <span>{t("name")}</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </label>
        <div className="field-row">
          <label className="field">
            <span>{t("qty")}</span>
            <input className="input" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
          </label>
          <label className="field">
            <span>{t("unit")}</span>
            <select className="input" value={unit} onChange={(e) => setUnit(e.target.value)}>
              <option value="">—</option>
              {UNIT_CODES.map((u) => (
                <option key={u} value={u}>
                  {unitLabel(u)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span>{t("comment")}</span>
          <textarea className="input" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={500} />
        </label>

        <div className="field">
          <span>{t("photo")}</span>
          {item.photoFileId && <img className="photo-preview" src={fileUrl(item.photoFileId, token)} alt="" />}
          <div className="photo-actions">
            <label className={online && !uploading ? "btn" : "btn disabled"}>
              📷 {uploading ? t("uploading") : t("addPhoto")}
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={!online || uploading}
                onChange={(e) => void onPhoto(e.target.files?.[0])}
              />
            </label>
            {item.photoFileId && (
              <button type="button" className="btn" onClick={() => void engine.mutate({ type: "item.update", id: item.id, photoFileId: null })}>
                {t("removePhoto")}
              </button>
            )}
          </div>
          {(!online || error) && <div className="hint">{error ?? t("photoOffline")}</div>}
        </div>

        <div className="field-row">
          <button
            type="button"
            className="btn danger"
            onClick={() => {
              void engine.mutate({ type: "item.delete", id: item.id });
              onClose();
            }}
          >
            {t("delete")}
          </button>
          <button type="submit" className="btn btn-primary">
            {t("save")}
          </button>
        </div>
      </form>
    </Sheet>
  );
}
